/**
 * 在頁面檔案中定義權限，例如需要登入：
 * 
 * definePageMeta({
 *   requirePermissions: true,
 * })
 * 
 * 在頁面檔案中定義權限，例如需要登入，且具有 foo 或 bar 權限：
 * 
 * definePageMeta({
 *   requirePermissions: ['foo', 'bar'],
 * })
 * 
 * 在頁面檔案中定義頁面支援的語系，例如只有中文內容：
 * 
 * definePageMeta({
 *   locales: ['zh'],
 * })
 * 
 */
export default defineNuxtPlugin((nuxtApp) => {

  const router = useRouter()
  const authLock = ref(false) // 鎖定正在進行登入流程

  // 紀錄目前瀏覽頁面的所需權限
  const currentRequiredPermissions = usePagePermission()

  // 在 plugin 頂層解析 composable，避免在 guard / watcher 重新執行時失去 Nuxt context，
  // 同時避免 useMember 內部的 watchEffect 被重複註冊
  const member = useMember()
  const route = useRoute()
  const localePath = useLocalePath()
  const { locale } = (nuxtApp as any).$i18n

  // 進入每個頁面時，檢查會員權限
  router.beforeEach((to, from, next) => {

    authLock.value = true

    const requirePermissions = to.path.replace(/^\/(zh|en)/, '').startsWith('/admin')
      ? [MemberGroup.Admin]
      : to.matched[0]?.meta.requirePermissions as string[] | undefined
    currentRequiredPermissions.value = requirePermissions

    useMemberAsync({
      require: currentRequiredPermissions.value as any,
      navigationGuard: {
        to, next
      },
    }).finally(() => {
      authLock.value = false
    })

  })

  // 進入頁面前，檢查會員的語系瀏覽權限，如果沒有，就跳到許可的語言
  router.beforeEach((to, from, next) => {
    const currentLocale = locale.value as 'zh' | 'en'
    if (member.value && !member.value.locales.includes(currentLocale)) {
      const availableLocale = member.value.locales[0]
      return next(changeRouteToLang(to.fullPath, availableLocale))
    }
    next()
  })

  // 檢查頁面支援的語系，若目前語系不在支援清單內，跳回首頁
  router.beforeEach((to, from, next) => {
    const pageLocales = to.matched[0]?.meta.locales as string[] | undefined
    if (pageLocales && pageLocales.length > 0) {
      const currentLocale = locale.value as string
      if (!pageLocales.includes(currentLocale)) {
        return next(useLocalePath()('/'))
      }
    }
    next()
  })

  router.isReady().then(() => {
    // 隨時檢查所需權限與目前會員的匹配
    watchEffect(() => {
      if (currentRequiredPermissions.value && !member.value && !authLock.value) {
        useMemberAsync({
          require: currentRequiredPermissions.value as any
        })
      }
    })

    // 當會員資料載入後（含全頁重載），若需強制重設密碼則攔截
    watchEffect(() => {
      if (member.value?.password_renew_request && !route.path.includes('/member/')) {
        navigateTo(localePath('/member/passwd'))
      }
    })
  })

})

// 要有更聰明的方法來轉換
function changeRouteToLang(routePath: string, lang: 'zh' | 'en') {
  return routePath.replace(/^\/?(zh|en)/, `/${lang}`)
}
